import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getAvailableLanguages, setCurrentLanguage, getCurrentLanguage, resolveAutoLanguage } from '../utils/languageDetection';

interface I18nContextType {
  currentLanguage: string;
  actualLanguage: string;
  availableLanguages: Record<string, string>;
  changeLanguage: (language: string) => Promise<void>;
  isLoading: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: React.ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  
  const availableLanguages = useMemo(() => getAvailableLanguages(), []);
  const availableLanguageCodes = useMemo(() => Object.keys(availableLanguages), [availableLanguages]);
  
  // Initialize language synchronously to avoid empty preferredLanguage on first render
  const [currentLanguage, setCurrentLanguageState] = useState<string>(() => {
    const detectedLanguage = getCurrentLanguage(availableLanguageCodes);
    return detectedLanguage;
  });
  
  const [actualLanguage, setActualLanguage] = useState<string>(() => {
    const detectedLanguage = getCurrentLanguage(availableLanguageCodes);
    // If the detected language is 'auto', resolve it to the actual browser language
    return detectedLanguage === 'auto'
      ? resolveAutoLanguage(availableLanguageCodes)
      : detectedLanguage;
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize language on mount
  useEffect(() => {
    if (isInitialized) return; // Prevent multiple initializations

    const initializeLanguage = async () => {
      setIsLoading(true);
      try {
        const detectedLanguage = getCurrentLanguage(availableLanguageCodes);

        // If the detected language is 'auto', resolve it to the actual browser language
        const actualLanguage = detectedLanguage === 'auto'
          ? resolveAutoLanguage(availableLanguageCodes)
          : detectedLanguage;

        await i18n.changeLanguage(actualLanguage);
        setCurrentLanguageState(detectedLanguage); // Store 'auto' in state to show in UI
        setActualLanguage(actualLanguage); // Store the actual language
        setCurrentLanguage(detectedLanguage); // Store 'auto' in localStorage if that's what was detected
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize language:', error);
        // Fallback to English
        try {
          await i18n.changeLanguage('en');
          setCurrentLanguageState('en');
          setActualLanguage('en');
          setCurrentLanguage('en');
          setIsInitialized(true);
        } catch (fallbackError) {
          console.error('Failed to fallback to English:', fallbackError);
          setIsInitialized(true); // Still mark as initialized to prevent further attempts
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeLanguage();
  }, [i18n, availableLanguageCodes, isInitialized]);

  const changeLanguage = useCallback(async (language: string): Promise<void> => {
    if (!availableLanguageCodes.includes(language)) {
      console.warn(`Language '${language}' is not available. Available languages:`, availableLanguageCodes);
      return;
    }

    try {
      setIsLoading(true);

      // If the selected language is 'auto', resolve it to the actual browser language
      const actualLanguage = language === 'auto'
        ? resolveAutoLanguage(availableLanguageCodes)
        : language;

      await i18n.changeLanguage(actualLanguage);
      setCurrentLanguageState(language); // Store the selected language ('auto' or specific) in state
      setActualLanguage(actualLanguage); // Store the actual language
      setCurrentLanguage(language); // Store the selected language in localStorage
    } catch (error) {
      console.error('Failed to change language:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [i18n, availableLanguageCodes]);

  const contextValue: I18nContextType = useMemo(() => ({
    currentLanguage,
    actualLanguage,
    availableLanguages,
    changeLanguage,
    isLoading,
  }), [currentLanguage, actualLanguage, availableLanguages, changeLanguage, isLoading]);

  return (
    <I18nContext.Provider value={contextValue}>
      {children}
    </I18nContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};